import { motion } from 'framer-motion'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import GlowButton from '../components/shared/GlowButton'
import { Icons } from '../components/shared/Icons'
import './DraftPages.css'

const fourPillars = [
  {
    title: 'Ground Intelligence',
    subtitle: 'Continuous Monitoring',
    icon: Icons.layers,
    color: 'var(--color-energy)',
    bgColor: 'rgba(0, 255, 136, 0.1)',
    features: [
      'Real-time data via Modbus TCP & Huawei SDongle push',
      '30+ live metrics with sub-second latency',
      'Battery SOC/SOH monitoring & control',
      'DC string analysis with MPPT tracking',
      'Three-phase power quality monitoring',
      'Environmental sensor integration',
      'Portfolio-level benchmarking & ESG tracking'
    ],
    stats: { value: '34', label: 'Hardware Endpoints' }
  },
  {
    title: 'Aerial Intelligence',
    subtitle: 'Thermal & EL Imaging',
    icon: Icons.drone,
    color: 'var(--color-drone)',
    bgColor: 'rgba(0, 212, 255, 0.1)',
    features: [
      'YOLOv8-powered defect detection (98% accuracy)',
      'FLIR thermal imaging (320x256 radiometric)',
      'EL (electroluminescence) micro-crack detection',
      'Automated monthly inspection scheduling',
      'Hotspot detection with ±5°C accuracy',
      '< 200ms edge AI processing'
    ],
    stats: { value: '94%', label: 'Detection Accuracy' }
  },
  {
    title: 'Satellite Intelligence',
    subtitle: '40 Years of Data',
    icon: Icons.satellite,
    color: 'var(--color-space-accent)',
    bgColor: 'rgba(139, 92, 246, 0.1)',
    features: [
      'NASA POWER irradiance integration',
      'Copernicus Sentinel NDVI analysis',
      'Soiling detection from space',
      'Expected vs. actual performance comparison',
      'Historical weather correlation',
      'Free data access - no sensors required'
    ],
    stats: { value: '40yr', label: 'Historical Data' }
  },
  {
    title: 'AI Fusion Engine',
    subtitle: 'Multi-Modal Prediction',
    icon: Icons.brain,
    color: 'var(--color-solar)',
    bgColor: 'rgba(255, 215, 0, 0.1)',
    features: [
      '30-180 day failure prediction horizon',
      '< 5% false positives (vs. 40-60% industry)',
      '6 ML models working in fusion',
      'Automated work order generation',
      'Priority scoring: Severity x Revenue x Weather',
      'Continuous learning from field data'
    ],
    stats: { value: '< 5%', label: 'False Positives' }
  }
]

const mlModels = [
  {
    name: 'Drone Defect Detector',
    type: 'YOLOv8',
    accuracy: '98%',
    description: 'Real-time thermal anomaly detection from drone imagery. Identifies hotspots, cracks, and discoloration.',
    icon: Icons.drone
  },
  {
    name: 'Generation Forecaster',
    type: 'LSTM',
    accuracy: '94%',
    description: 'Predicts energy generation based on weather patterns, historical performance, and seasonal trends.',
    icon: Icons.trending
  },
  {
    name: 'Failure Predictor',
    type: 'Classification',
    accuracy: '91%',
    description: 'Identifies components at risk of failure 30-180 days in advance using multi-modal data fusion.',
    icon: Icons.warning
  },
  {
    name: 'Fault Classifier',
    type: 'Classification',
    accuracy: '96%',
    description: 'Categorizes detected faults into 16+ categories for targeted maintenance response.',
    icon: Icons.layers
  },
  {
    name: 'Battery Optimizer',
    type: 'MILP',
    accuracy: 'Optimal',
    description: 'Optimizes battery dispatch schedules based on tariffs, grid demand, and solar forecasts.',
    icon: Icons.battery
  },
  {
    name: 'Anomaly Detector',
    type: 'Pattern Recognition',
    accuracy: '93%',
    description: 'Detects subtle performance deviations that precede equipment failures.',
    icon: Icons.alert
  }
]

const featureCategories = [
  {
    title: 'Real-Time Monitoring',
    features: [
      'Live power flow dashboard (Solar, Grid, Load, Battery cards)',
      'MPPT string performance grid (up to 52 strings)',
      'Power flow history chart & 7-day performance comparison',
      'Battery SOC gauge with capacity, voltage & temperature',
      'Smart meter & grid meter data (DTSU666-H)',
      'Inverter diagnostics & health (temperature, insulation, fault codes)',
      'Self-consumption, autonomy & efficiency metrics'
    ]
  },
  {
    title: 'Predictive Maintenance',
    features: [
      '30-180 day failure prediction horizon',
      'RUL (Remaining Useful Life) estimator',
      'Risk ranking dashboard with priority scoring',
      'Component health scoring across all assets',
      'Degradation trend charts with projections',
      'Predictive maintenance scheduling'
    ]
  },
  {
    title: 'Fault Detection & Diagnostics',
    features: [
      'Fault classification with 16+ fault types, urgency distribution & trend charts',
      'Critical faults table with grouped/filtered views & fault statistics',
      'Derating detection with energy loss tracking & statistics by inverter',
      'Derating trend chart (30-day dual-axis: event count + energy loss)',
      'Fuse health by string with health scores, failure probability & priority',
      'Fuse replacement recommendations & per-string health trend charts'
    ]
  },
  {
    title: 'Alerts & Notifications',
    features: [
      'Alert configuration with threshold rules per alert type',
      'Alert history with grouped accordion & list views',
      '20+ inverter-specific alert types (voltage, current, string imbalance)',
      'Battery & thermal-specific alert subsystems',
      'Multi-channel delivery (Email, SMS, Push)',
      'Alert fatigue prevention (< 5% false positives)'
    ]
  },
  {
    title: 'Maintenance Management',
    features: [
      'Auto-generated work orders',
      'Technician assignment & scheduling',
      'Priority-based task queues',
      'Maintenance templates library',
      'Cost tracking & estimation',
      'Mobile workforce app'
    ]
  },
  {
    title: 'Reporting & Analytics',
    features: [
      'PDF/CSV/Excel export',
      'Advanced report builder UI',
      'Visual inspection log with images',
      'Event log with full audit trail',
      'Performance benchmarking',
      'Historical data reporting & trends'
    ]
  },
  {
    title: 'Mobile Apps',
    features: [
      '61 screens (iOS & Android)',
      'Single React Native codebase',
      'Offline mode support',
      'Push notifications',
      'Field inspection tools',
      'Role-based access control'
    ]
  },
  {
    title: 'Portfolio & ESG',
    features: [
      'Portfolio overview with 8 KPI cards & plant health scores',
      'Benchmark scorecard with radar chart & plant rankings',
      'ROI dashboard with cash flow chart, revenue/cost breakdowns & LCOE',
      'Carbon impact with CO2 trend, environmental equivalencies & certifications',
      'Real-time inverter benchmarks (efficiency, power factor, frequency)',
      'Regional comparison & metric-vs-median analysis'
    ]
  },
  {
    title: 'Visual Diagnostics & Maps',
    features: [
      'Interactive solar plant map',
      'EL (electroluminescence) image viewer',
      'Thermal image viewer with annotations',
      'DC I/U curve graphing',
      'Integrated component diagnostic view',
      'Solar panel map with health overlay'
    ]
  },
  {
    title: 'Weather & Environmental',
    features: [
      'Real-time weather map integration',
      'Temperature trend charts',
      'Humidity & irradiance monitoring',
      'Satellite irradiance comparison',
      'Environmental sensor monitoring',
      'Soiling detection & loss gauging'
    ]
  },
  {
    title: 'Installation & Assembly',
    features: [
      'SMM assembly guide with steps',
      'Drilling diagram generator',
      'String sizing analysis tool',
      'Visual inspection logging',
      'Field documentation tools'
    ]
  },
  {
    title: 'Administration & Configuration',
    features: [
      '6-tier role-based access control (RBAC)',
      'Setup wizard for new installations',
      'Multi-site & inverter configuration',
      'User management with customer accounts',
      'Audit log with full activity trail',
      'License & integration management'
    ]
  }
]

export default function Features() {
  return (
    <div className="draft-page">
      {/* Hero Section */}
      <section className="draft-hero">
        <div className="container">
          <ScrollReveal>
            <span className="draft-badge">Beyond Basic Monitoring</span>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="draft-title">
              Know what will fail{' '}
              <GradientText gradient="solar">before it happens.</GradientText>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="draft-subtitle">
              Most monitoring platforms tell you what happened. AI4Solar tells you what will happen—and what to do about it.
              Predictive AI + Satellite Data + Drone Thermal in one platform.
            </p>
          </ScrollReveal>

          {/* Key Stats */}
          <ScrollReveal delay={0.3}>
            <div className="hero-metrics" style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 'var(--space-3xl)',
              marginTop: 'var(--space-3xl)',
              flexWrap: 'wrap'
            }}>
              <div style={{ textAlign: 'center' }}>
                <span style={{
                  display: 'block',
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  color: 'var(--color-solar)'
                }}>6</span>
                <span style={{ color: 'var(--color-gray-400)', fontSize: '0.875rem' }}>ML Models</span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <span style={{
                  display: 'block',
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  color: 'var(--color-energy)'
                }}>&lt;5%</span>
                <span style={{ color: 'var(--color-gray-400)', fontSize: '0.875rem' }}>False Positives</span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <span style={{
                  display: 'block',
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  color: 'var(--color-drone)'
                }}>110+</span>
                <span style={{ color: 'var(--color-gray-400)', fontSize: '0.875rem' }}>Dashboard Components</span>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Four Pillars Section */}
      <section className="platform-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Land. Air. Space. AI.</h2>
            <p>Four intelligence layers working together to give you complete visibility.</p>
          </ScrollReveal>

          <div className="pillars-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--space-xl)'
          }}>
            {fourPillars.map((pillar, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div
                  className="pillar-card"
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: 'var(--radius-xl)',
                    padding: 'var(--space-2xl)',
                  }}
                  whileHover={{
                    borderColor: pillar.color,
                    y: -5
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-lg)', marginBottom: 'var(--space-xl)' }}>
                    <div style={{
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: pillar.bgColor,
                      borderRadius: 'var(--radius-lg)',
                      color: pillar.color,
                      flexShrink: 0
                    }}>
                      <span style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{pillar.icon}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: 'var(--color-white)',
                        marginBottom: '0.25rem'
                      }}>{pillar.title}</h3>
                      <span style={{
                        fontSize: '0.875rem',
                        color: pillar.color
                      }}>{pillar.subtitle}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{
                        display: 'block',
                        fontSize: '1.5rem',
                        fontWeight: 800,
                        color: pillar.color
                      }}>{pillar.stats.value}</span>
                      <span style={{
                        fontSize: '0.75rem',
                        color: 'var(--color-gray-500)'
                      }}>{pillar.stats.label}</span>
                    </div>
                  </div>

                  <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                    {pillar.features.map((feature, j) => (
                      <li key={j} style={{
                        color: 'var(--color-gray-400)',
                        fontSize: '0.9375rem',
                        paddingLeft: '1.5rem',
                        position: 'relative'
                      }}>
                        <span style={{
                          position: 'absolute',
                          left: 0,
                          color: pillar.color
                        }}>+</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ML Models Section */}
      <section className="platform-section platform-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>6 ML Models. One Mission.</h2>
            <p>Purpose-built machine learning for solar operations. From computer vision to optimization.</p>
          </ScrollReveal>

          <div className="ml-models-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 'var(--space-lg)'
          }}>
            {mlModels.map((model, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <motion.div
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: 'var(--radius-xl)',
                    padding: 'var(--space-xl)',
                    height: '100%'
                  }}
                  whileHover={{ y: -3 }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--space-md)'
                  }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255, 215, 0, 0.1)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--color-solar)'
                    }}>
                      <span style={{ width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{model.icon}</span>
                    </div>
                    <span style={{
                      background: 'rgba(0, 255, 136, 0.1)',
                      color: 'var(--color-energy)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: 'var(--radius-full)',
                      fontSize: '0.875rem',
                      fontWeight: 600
                    }}>{model.accuracy}</span>
                  </div>

                  <h4 style={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: 'var(--color-white)',
                    marginBottom: '0.25rem'
                  }}>{model.name}</h4>
                  <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--color-gray-500)',
                    display: 'block',
                    marginBottom: 'var(--space-sm)'
                  }}>{model.type}</span>
                  <p style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-gray-400)',
                    lineHeight: 1.6
                  }}>{model.description}</p>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Categories Grid */}
      <section className="features-grid-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Everything You Need</h2>
            <p>Comprehensive tools for the complete solar operations lifecycle.</p>
          </ScrollReveal>

          <div className="features-categories">
            {featureCategories.map((category, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div
                  className="feature-category-card"
                  whileHover={{ y: -5 }}
                >
                  <h3>{category.title}</h3>
                  <ul>
                    {category.features.map((feature, j) => (
                      <li key={j}>{feature}</li>
                    ))}
                  </ul>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Not Basic Monitoring */}
      <section className="platform-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Why Basic Monitoring Isn't Enough</h2>
            <p>Only 38% of solar assets have AI-based diagnostics. 61% of companies struggle to integrate predictive analytics.</p>
          </ScrollReveal>

          <div className="problem-stats-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 'var(--space-xl)',
            marginBottom: 'var(--space-3xl)'
          }}>
            {[
              { value: '40-60%', label: 'Basic Monitoring False Positives', bad: true },
              { value: '72hrs', label: 'Industry Detection-to-Repair', bad: true },
              { value: '< 5%', label: 'AI4Solar False Positive Rate', good: true },
              { value: '24hrs', label: 'AI4Solar Detection-to-Repair', good: true }
            ].map((stat, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div style={{
                  textAlign: 'center',
                  padding: 'var(--space-xl)',
                  background: stat.bad ? 'rgba(255, 107, 107, 0.05)' : 'rgba(0, 255, 136, 0.05)',
                  border: `1px solid ${stat.bad ? 'rgba(255, 107, 107, 0.2)' : 'rgba(0, 255, 136, 0.2)'}`,
                  borderRadius: 'var(--radius-xl)'
                }}>
                  <span style={{
                    display: 'block',
                    fontSize: '2rem',
                    fontWeight: 800,
                    color: stat.bad ? '#ff6b6b' : 'var(--color-energy)',
                    marginBottom: 'var(--space-sm)'
                  }}>{stat.value}</span>
                  <span style={{
                    fontSize: '0.875rem',
                    color: 'var(--color-gray-400)'
                  }}>{stat.label}</span>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={0.2}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 'var(--radius-xl)',
              padding: 'var(--space-2xl)',
              textAlign: 'center'
            }}>
              <p style={{ color: 'var(--color-gray-300)', fontSize: '1.125rem', lineHeight: 1.7, marginBottom: 'var(--space-lg)' }}>
                <strong style={{ color: 'var(--color-white)' }}>Predictive maintenance reduces costs by 25-30%</strong> and cuts maintenance expenses by up to 40% with computer vision AI.
                Companies implementing predictive maintenance increase asset lifespan by 20% and squeeze an extra 20-40% of life from equipment.
              </p>
              <p style={{ color: 'var(--color-gray-500)', fontSize: '0.875rem' }}>
                Source: Industry benchmarks from SmartHelio, Precedence Research, and Intelliarts
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Platform Preview Section */}
      <section className="platform-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>See the Platform in Action</h2>
            <p>Powerful dashboards designed for solar professionals. Dark and light modes included.</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="platform-preview" style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--space-xl)',
              marginTop: 'var(--space-2xl)'
            }}>
              <motion.div
                style={{
                  borderRadius: 'var(--radius-xl)',
                  overflow: 'hidden',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
                }}
                whileHover={{ y: -5, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="/images/platform/energy-forecasting-dark.png"
                  alt="Energy Forecasting Dashboard - Dark Mode"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                <div style={{
                  padding: 'var(--space-lg)',
                  background: 'rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(10px)'
                }}>
                  <h4 style={{ color: 'var(--color-white)', marginBottom: '0.25rem' }}>Energy Forecasting</h4>
                  <p style={{ color: 'var(--color-gray-400)', fontSize: '0.875rem' }}>ML-powered generation predictions with grid integration</p>
                </div>
              </motion.div>

              <motion.div
                style={{
                  borderRadius: 'var(--radius-xl)',
                  overflow: 'hidden',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
                }}
                whileHover={{ y: -5, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="/images/platform/dashboard-performance-dark.png"
                  alt="Performance Dashboard - Dark Mode"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                <div style={{
                  padding: 'var(--space-lg)',
                  background: 'rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(10px)'
                }}>
                  <h4 style={{ color: 'var(--color-white)', marginBottom: '0.25rem' }}>Performance Dashboard</h4>
                  <p style={{ color: 'var(--color-gray-400)', fontSize: '0.875rem' }}>Real-time monitoring with detailed analytics</p>
                </div>
              </motion.div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="draft-cta">
        <div className="container">
          <ScrollReveal className="draft-cta-content">
            <h2>See all features in action</h2>
            <p>Book a personalized demo and explore how AI4Solar can transform your operations.</p>
            <GlowButton href="/demo" variant="solar" size="large">
              Request Demo
            </GlowButton>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
